import { FC, useMemo } from "react";
import { Stack, Typography } from "@mui/material";
import { useAppSelector } from "@/store/hooks";
import { money } from "@/utils/numeral/money";
import { ResourceType } from "@/services/cloudchipr.api";
import { IconByResourceType } from "@/components/pages/common/IconByResourceType";
import { getProviderTypeByResourceType } from "@/utils/helpers/providers/getProviderTypeByResourceType";
import { resourceGroupsByProvider } from "@/utils/constants/resources/resourceGroupsByProvider";
import { resourceServices } from "@/utils/constants/resources/resources";
import { getResourceTypeName } from "@/utils/helpers/resources/getResourceTypeName";
import { gridResourcesCurrentCleanResourcesCountSelector } from "@/store/resources-grid/selectors/clean/resource-related/gridResourcesCurrentCleanResourcesCountSelector";
import { gridResourcesCurrentCleanResourceTypeSelector } from "@/store/resources-grid/selectors/clean/resource-related/gridResourcesCurrentCleanResourceTypeSelector";
import { isStringResourceType } from "@/utils/helpers/resources/isStringResourceType";
import { gridResourcesCurrentCleanResourcesPricePerMonthSelector } from "@/store/resources-grid/selectors/clean/resource-related/gridResourcesCurrentCleanResourcesPricePerMonthSelector";

export const ResourceGridCleanDialogDescription: FC = () => {
  const totalEms = useAppSelector(
    gridResourcesCurrentCleanResourcesPricePerMonthSelector,
  );

  const resourceType = useAppSelector(
    gridResourcesCurrentCleanResourceTypeSelector,
  );

  const resourcesCount = useAppSelector(
    gridResourcesCurrentCleanResourcesCountSelector,
  );

  const serviceType = useMemo(() => {
    if (!resourceType) {
      return resourceType;
    }

    const provider = getProviderTypeByResourceType(resourceType);

    if (!provider) {
      return resourceType;
    }

    const groups = resourceGroupsByProvider.get(provider);

    if (!groups) {
      return resourceType;
    }

    return (
      groups.find((group) => {
        return (
          group.value === resourceType ||
          group.resourceTypes.includes(resourceType)
        );
      })?.value ?? resourceType
    );
  }, [resourceType]) as ResourceType;

  if (!serviceType || !isStringResourceType(resourceType)) {
    return null;
  }

  return (
    <Stack px={3} pt={3} pb={2} spacing={3}>
      <Typography
        component="span"
        variant="body1"
        color="text.primary"
        fontWeight="bold"
      >
        {money(totalEms)} Monthly Price
      </Typography>

      <Stack direction="row" alignItems="center" spacing={0.5}>
        {serviceType && resourceServices.has(serviceType) && (
          <IconByResourceType resourceType={serviceType} />
        )}

        <Typography sx={{ m: 0 }} variant="body2" fontWeight={500}>
          {getResourceTypeName(resourceType, {
            service: true,
            singular: true,
            type: "abbreviation",
          })}{" "}
          - {resourcesCount}{" "}
          {getResourceTypeName(resourceType, {
            singular: resourcesCount === 1,
          })}
        </Typography>
      </Stack>
    </Stack>
  );
};
